/// <amd-module name="Likes/StatefulLikeHeart" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssSecurityService } from 'VSS/Platform/Security';

import { SocialEngagementCreateParameter } from "Likes/Contracts/Social";
import { LikeActionCreator } from "Likes/LikeActionCreator";
import { LikeActions, LikeState } from "Likes/ActionsHub";
import { LikeHeart } from "Likes/LikeHeart";
import { LikeArtifactInfo } from "Likes/LikeModels";
import { LikeStore } from "Likes/LikeStore";

export interface StatefulLikeHeartProps extends IVssComponentProperties {
    likeArtifactInfo: LikeArtifactInfo;
    ariaLabelLiked?: string;
    ariaLabelUnLiked?: string;
    serviceInstanceId?: string; // take service to call in the form of service instance Id (on empty go to TFS)
    className?: string;
    initialLikeInfo?: LikeState;
    onLikeHeartRender?(): void; // Callback - called if likeHeart will get rendered
}

export interface StatefulLikeHeartState extends LikeState {
}

const socialEngagementSecuityNameSpace = "81c27cc8-7a9f-48ee-b63f-df1e1d0412dd";
const socialEngagementToken = "/Social";

/**
 * Shared like "heart" view to display like heart for any kind of likable artifact
 */
export class StatefulLikeHeart extends VssComponent<StatefulLikeHeartProps, StatefulLikeHeartState> {
    public static componentType = "statefulLikeHeart";

    private _actionsCreator: LikeActionCreator;
    private _store: LikeStore;
    private _hasReadPermission: boolean;
    private _pageContext: IVssPageContext;

    constructor(props: StatefulLikeHeartProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        const securityService: IVssSecurityService = this._pageContext.getService<IVssSecurityService>("IVssSecurityService");
        this._hasReadPermission = securityService.hasPermission(socialEngagementSecuityNameSpace, socialEngagementToken, 1 /* Read */);

        if (!this._hasReadPermission) {
            return;
        }


        let initialState: StatefulLikeHeartState;
        if (props.initialLikeInfo) {
            initialState = props.initialLikeInfo;
        } else {
            initialState = {
                isLiked: false,
                likeCount: 0
            };
        }
        this.state = initialState;
        this._initializeFlux(initialState);
        if (!props.initialLikeInfo) {
            this._actionsCreator.getLikes(this.props.likeArtifactInfo);
        }
    }

    public componentDidMount(): void {
        if (!this._hasReadPermission) {
            return;
        }

        this._store.addChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element | null {

        if (!this._hasReadPermission) {
            return null;
        }

        if (this.props.onLikeHeartRender) {
            this.props.onLikeHeartRender();
        }

        return (
            !this._store.isLoading()
                ? <LikeHeart
                    className={this.props.className}
                    onLikeHeartClick={this._onLikeHeartClick}
                    isLiked={this.state.isLiked}
                    ariaLabelLiked={this.props.ariaLabelLiked}
                    ariaLabelUnLiked={this.props.ariaLabelUnLiked}
                    likedBy={this.state.topLikedUsers}
                    fetchLikedUsers={this._onFetchLikedUsers}
                    likedCount={this.state.likeCount} />
                : <div />
        );
    }

    public componentWillUnmount(): void {
        if (!this._hasReadPermission) {
            return;
        }

        this._store.removeChangedListener(this._onStoreChanged);
        this._store.dispose();
    }

    private _initializeFlux(initialState: StatefulLikeHeartState): void {
        const actions = new LikeActions();
        this._store = new LikeStore(actions, initialState);
        this._actionsCreator = new LikeActionCreator(actions, this._pageContext);
    }

    private _onLikeHeartClick = (isLiked: boolean): void => {
        if (isLiked) {
            this._actionsCreator.createLike(this.props.likeArtifactInfo);
        } else {
            this._actionsCreator.deleteLike(this.props.likeArtifactInfo);
        }
    }

    private _onFetchLikedUsers = (): void => {
        this._actionsCreator.fetchLikedUsers(this.props.likeArtifactInfo);
    }

    private _onStoreChanged = (): void => {
        this.setState(this._store.getState());
    }
}

VssComponent.register(StatefulLikeHeart.componentType, StatefulLikeHeart);
